import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, filter, race, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TokenRefreshState } from '../services/token-refresh.state';

const EXTERNAL_DOMAINS = ['maps.googleapis.com', 'nominatim.openstreetmap.org', 'tiles.openfreemap.org', 'bkd-tms.urbanoexpress.com.pe', 'api.geo.dev-urbano.dev'];
// Únicos endpoints de /auth/ que NO deben disparar el flujo de refresh (no requieren
// sesión activa). Otros endpoints como /auth/change-password sí están autenticados
// y deben poder refrescar el token o forzar logout ante un 401.
const AUTH_ENDPOINTS_WITHOUT_REFRESH = ['/auth/login', '/auth/refresh'];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // No adjuntar token a peticiones externas (mapas, geocoding)
  if (EXTERNAL_DOMAINS.some(domain => req.url.includes(domain))) {
    return next(req);
  }

  const authService    = inject(AuthService);
  const refreshState   = inject(TokenRefreshState);

  const token = authService.getAccessToken();
  const authReq = token ? withBearer(req, token) : req;

  return next(authReq).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !AUTH_ENDPOINTS_WITHOUT_REFRESH.some((endpoint) => req.url.includes(endpoint))
      ) {
        return handle401(authReq, next, authService, refreshState);
      }
      return throwError(() => error);
    }),
  );
};

function withBearer(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  state: TokenRefreshState,
): Observable<HttpEvent<unknown>> {
  if (!state.isRefreshing) {
    state.isRefreshing = true;
    state.subject$.next(null);

    return authService.refreshToken().pipe(
      switchMap((newToken) => {
        state.isRefreshing = false;
        state.subject$.next(newToken);
        return next(withBearer(req, newToken));
      }),
      catchError((err) => {
        state.isRefreshing = false;
        authService.logout();
        state.refreshFailed$.next();
        return throwError(() => err);
      }),
    );
  }

  return race(
    state.subject$.pipe(
      filter((token): token is string => token !== null),
      take(1),
    ),
    state.refreshFailed$.pipe(
      take(1),
      switchMap(() => throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Session expired' }))),
    ),
  ).pipe(switchMap((token) => next(withBearer(req, token))));
}
